{ unitFeatures, segmentUnits, signatureOf, labelSections, assignRoles, MAX_SECTION_BARS } from @danielx/song-structure

{ buildNoteIndex, type IndexedNote } from ./note-index.civet
{ measureCount } from ./measures.civet
type { PracticeMetadata } from ./practice-metadata.civet

type MidiSong = import('./song.civet').default

{ceil, max, min, round} := Math

// Auto practice config: a song straight off a MIDI import, turned into
// something playable without the setup dialog. The same structural analysis
// chip's arrange lab runs (@danielx/song-structure) finds the sections and
// picks the melody channel as the part you play; everything else becomes the
// backing track. It's a starting point, not a verdict — the practice setup
// editor edits whatever this writes.
//
// The row grid is eighth notes against the FIRST time signature: enough
// resolution for the presence/onset features, and sections land on integer
// measures anyway (they're for section drills, not fractional-bar pickups).
// A mid-song meter change makes the bar grid approximate past it, which the
// DP tolerates — boundaries stay where the evidence is, only the phrase-grid
// discount drifts.
ROWS_PER_BEAT := 2

export autoPracticeMetadata := (song: MidiSong): PracticeMetadata? ->
  notes := buildNoteIndex song
  return unless notes.length

  row := (beat: number) -> round beat * ROWS_PER_BEAT

  // Channel 9 is GM percussion: rhythm evidence for sectioning, never a
  // playable part
  melodic := new Map<number, {startTick: number, endTick: number, pitch: number}[]>
  drums: {startTick: number}[] := []
  for note of notes
    {c, n, startBeat, endBeat} := note
    if c is 9
      drums.push {startTick: row startBeat}
    else
      list := melodic.get(c) ?? []
      melodic.set c, list
      list.push {startTick: row(startBeat), endTick: max(row(startBeat) + 1, row endBeat), pitch: n}
  return unless melodic.size

  ts := song.timeSignatureMap[0]
  beatsPerMeasure := if ts then ts.numerator * 4 / ts.denominator else 4
  rowsPerBar := max 2, round beatsPerMeasure * ROWS_PER_BEAT
  barsPerUnit := if rowsPerBar % 2 is 0 then 0.5 else 1
  unitRows := round rowsPerBar * barsPerUnit

  songBeats := song.beatLength()
  totalRows := max unitRows, ceil songBeats * ROWS_PER_BEAT
  totalBars := max 1, ceil totalRows / rowsPerBar
  totalUnits := max 1, ceil totalRows / unitRows

  // ── sections ────────────────────────────────────────────────────────────────
  features := unitFeatures melodic, drums, 1, unitRows, totalUnits
  bounds := segmentUnits features, {maxUnits: round(MAX_SECTION_BARS / barsPerUnit), barsPerUnit}

  measures := measureCount song.timeSignatureMap, songBeats
  // Fractional-bar cuts (a pickup) snap to the measure they open
  startMeasures: number[] := []
  for b of bounds
    m := min measures, 1 + round b * barsPerUnit
    startMeasures.push m unless startMeasures.length and startMeasures.at(-1)! >= m

  voices := [...melodic.values()].map (list) ->
    raw: list.map ({startTick, pitch}) -> {row: startTick, pitch}
  rowOfMeasure := (i: number) ->
    if i >= startMeasures.length then totalRows else (startMeasures[i] - 1) * rowsPerBar
  labels := labelSections startMeasures.map (m, i) ->
    signatureOf voices, (m - 1) * rowsPerBar, rowOfMeasure(i + 1)

  sections := startMeasures.map (startMeasure, i) ->
    name: labels[i]
    startMeasure: startMeasure
    endMeasure: if i + 1 < startMeasures.length then startMeasures[i + 1] - 1 else measures

  // ── the part you play ───────────────────────────────────────────────────────
  roles := assignRoles melodic, totalBars, rowsPerBar, 1
  fallback := [...melodic.entries()].reduce (a, b) -> if a[1].length >= b[1].length then a else b
  melody := roles.melody ?? fallback[0]

  // Difficulty from how busy the part is: notes per second over the span it
  // actually plays, on Synthesia's 0-100 scale. ~2/s is a beginner tune,
  // ~8/s a hard one.
  playNotes := notes.filter (note) -> note.c is melody
  span := playNotes.at(-1)!.endTime - playNotes[0].startTime
  nps := if span > 0 then playNotes.length / span else 0
  difficulty := max 5, min 95, round 8 + nps * 9

  {
    version: 2
    title: song.title() or undefined
    difficulty
    parts: [{name: "You", channels: [melody]}]
    sections
  }

export default autoPracticeMetadata
